import React, { useEffect, useState } from "react";
import adminAxios from "../../config/adminAxios";
import { Box, Heading, Button, Grid, Text, Layer, Spinner } from "grommet";
import { MailOption } from "grommet-icons";
import { useNavigate, useParams } from "react-router-dom";
import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";

// Example stubs for any components you might be using
const VideoTutorialsGrid = () => null;

const PortfolioPage = () => {
  const { portfolio_id } = useParams();

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [portfolioName, setPortfolioName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch portfolio details (name) using the portfolio_id
  useEffect(() => {
    const fetchPortfolioName = async () => {
      setLoading(true);
      try {
        const response = await adminAxios.get(`/api/admin/portfolio/${portfolio_id}`);
        const data = response.data;
        setPortfolioName(data.portfolio_name);
      } catch (err) {
        console.error("Error fetching portfolio details:", err);
        setError("Failed to load portfolio.");
      } finally {
        setLoading(false);
      }
    };

    if (portfolio_id) {
      fetchPortfolioName();
    } else {
      setLoading(false);
    }
  }, [portfolio_id]);

  if (loading) {
    return (
      <Box fill align="center" justify="center" background="light-1">
        <Spinner size="large" />
        <Text margin={{ top: "small" }}>Loading portfolio...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box fill align="center" justify="center" background="light-1">
        <Text color="status-critical">{error}</Text>
      </Box>
    );
  }

  // Merge the Help card into the same array to ensure consistent width/placement
  const cardItems = [
    // {
    //   title: "Create Skill Flow",
    //   description:
    //     "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    //   buttonText: "Create Skill Flow",
    //   action: () => {
    //     navigate(`/admin/portfolio/${portfolio_id}/skill-flow`);
    //   },
    // },
    {
      title: "Manage Learning Paths",
      description:
        "Review and control your learning paths— edit details, toggle publish status, and introduce new paths to enhance your offerings.",
      buttonText: "Manage Learning Paths",
      action: () => {
        navigate(`/admin/portfolio/${portfolio_id}/learning-paths`);
      },
    },
    {
      title: "Manage Courses",
      description:
        "Access and update your portfolio's courses— view all courses, modify details, and add new ones as needed.",
      buttonText: "Manage Courses",
      action: () => {
        navigate(`/admin/portfolio/${portfolio_id}/manage-courses`);
      },
    },

    {
      title: "Skill Question Mapping",
      description:
        "Manage the alignment between skills and questions— view existing mappings, add targeted questions, and update mappings efficiently.",
      buttonText: "Skill Question Mapping",
      action: () => {
        navigate(`/admin/portfolio/${portfolio_id}/skill-question`);
      },
    },
    // HELP card included in the same array
    {
      title: "Help",
      description: (
        <>
          <Text
            color="brand"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => setShowEmailModal(true)}
          >
            Email Support
          </Text>
        </>
      ),
      // If you want a button on the Help card, add buttonText and handle it:
      // buttonText: "Contact Support",
      action: () => navigate(`/admin/contact`),
    },
  ];

  return (
    <Box fill="vertical" overflow="auto" background="light-1">
      <HeaderNavigation />

      <Box pad={{ horizontal: "large", vertical: "medium" }} flex="grow">
        <Box direction="row" justify="between" align="center" width="100%">
          {/* Back to Dashboard container with navigation */}
          <Box
            direction="row"
            align="center"
            gap="small"
            pad="small"
            background="white"
            elevation="small"
            round="small"
            border={{ color: "brand", size: "xsmall" }}
            width="auto"
            alignSelf="start"
            onClick={() => navigate("/admin/dashboard")}
            style={{ cursor: "pointer" }}
          >
            <Heading
              level="3"
              margin="none"
              color="brand"
              textAlign="start"
              responsive={false}
              truncate={false}
            >
              Back to Dashboard
            </Heading>
          </Box>
        </Box>

        <Box align="start" margin={{ top: "small", bottom: "small" }}>
          <Heading level="2">Actions for {portfolioName}</Heading>
        </Box>

        {/* Single grid for all cards */}
        <Grid
          columns={{ count: 3, size: "medium" }}
          gap="medium"
          justify="start"
          responsive
        >
          {cardItems.map((item, index) => (
            <Box
              key={index}
              elevation="small"
              round="small"
              pad="medium"
              background="white"
              border={{ color: "brand", size: "xsmall" }}
              width="100%"
              fill="vertical"
              direction="column"
              justify="between"
            >
              <Box>
                <Heading
                  level="3"
                  margin={{ bottom: "small" }}
                  textAlign="start"
                >
                  {item.title}
                </Heading>
                <Text textAlign="start">{item.description}</Text>
              </Box>
              <Box align="start">
                {item.buttonText && (
                  <Button
                    label={item.buttonText}
                    primary
                    onClick={item.action}
                    margin={{ top: "medium" }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Grid>
      </Box>
      <StickyFooter />

      {/* Email Support Modal */}
      {showEmailModal && (
        <Layer
          onEsc={() => setShowEmailModal(false)}
          onClickOutside={() => setShowEmailModal(false)}
        >
          <Box
            width="large"
            pad="medium"
            background="light-2"
            round="small"
            elevation="small"
          >
            <Heading level={2} margin="none" textAlign="center">
              Contact Us
            </Heading>
            <Text textAlign="center" size="large" margin={{ top: "small" }}>
              Need help with <strong>Skill Advisor</strong>? Get in touch with
              us for support.
            </Text>
            <Box align="center" margin={{ top: "medium" }}>
              <Button
                label="Email Support"
                icon={<MailOption />}
                href="mailto:hpe.skilladvisor@meteoros.in?&subject=Support%20needed%20for%20Skill%20Advisor&body=Hi%2C%0A%0APlease%20provide%20details%20of%20the%20issue%20you%20are%20facing%20with%20Skill%20Advisor%20in%20this%20email.%20We%20will%20review%20your%20request%2C%20and%20get%20back%20to%20you%20within%2048%20hours.%0A%0AIf%20you%20have%20any%20screenshots%20or%20media%20content%20to%20highlight%20your%20issue%2C%20please%20attach%20it%20to%20this%20email.%0A%0ABest%20Wishes%2C%0ASkill%20Advisor"
                primary
              />
            </Box>
            <Box align="center" margin={{ top: "medium" }}>
              <Button label="Close" onClick={() => setShowEmailModal(false)} />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default PortfolioPage;
